// CartContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext(null);

// ── Helpers: per-user cart keys ───────────────────────────────
const getUserKey = (suffix) => {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser') || 'null');
    const id = u?.id || u?.email || null;
    return id ? `cart_${suffix}_${id}` : null;
  } catch {
    return null;
  }
};

const loadPersistedCart = (suffix) => {
  const key = getUserKey(suffix);
  if (!key) return [];
  try { return JSON.parse(localStorage.getItem(key) || '[]'); }
  catch { return []; }
};

const savePersistedCart = (suffix, items) => {
  const key = getUserKey(suffix);
  if (key) localStorage.setItem(key, JSON.stringify(items));
};

const clearPersistedCart = (suffix, userId) => {
  // userId passed explicitly so we can clear even after currentUser is removed
  if (userId) localStorage.removeItem(`cart_${suffix}_${userId}`);
};

export function CartProvider({ children }) {
  const [cartItems,    setCartItems]    = useState([]);
  const [b2bCartItems, setB2bCartItems] = useState([]);
  const [isCartOpen,   setIsCartOpen]   = useState(false);
  const [toast,        setToast]        = useState({ visible: false, message: '' });
  const [userType,     setUserType]     = useState(() => localStorage.getItem('userType') || 'b2c');

  // ── Load cart for the currently logged-in user on mount ──────
  useEffect(() => {
    const b2c = loadPersistedCart('b2c');
    const b2b = loadPersistedCart('b2b');
    if (b2c.length) setCartItems(b2c);
    if (b2b.length) setB2bCartItems(b2b);
  }, []);

  // ── Reload cart when user logs in (auth-changed event) ───────
  useEffect(() => {
    const handleAuthChange = () => {
      const token = localStorage.getItem('token');
      if (token) {
        // User just logged in — restore their saved cart
        const b2c = loadPersistedCart('b2c');
        const b2b = loadPersistedCart('b2b');
        setCartItems(b2c);
        setB2bCartItems(b2b);
      }
      // On logout, logoutCart() already cleared state — nothing to do here
    };
    window.addEventListener('auth-changed', handleAuthChange);
    return () => window.removeEventListener('auth-changed', handleAuthChange);
  }, []);

  // ── Persist B2C cart whenever it changes ─────────────────────
  useEffect(() => {
    savePersistedCart('b2c', cartItems);
  }, [cartItems]);

  // ── Persist B2B cart whenever it changes ─────────────────────
  useEffect(() => {
    savePersistedCart('b2b', b2bCartItems);
  }, [b2bCartItems]);

  // ── Persist userType ─────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem('userType', userType);
  }, [userType]);

  const showToast = useCallback((message) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: '' }), 2000);
  }, []);

  const getCurrentCart = useCallback(() => {
    // Return combined cart with both B2C and B2B items
    return [...cartItems, ...b2bCartItems];
  }, [cartItems, b2bCartItems]);

  const addToCart = useCallback((product, quantity = 1, forceUserType = null) => {
    const currentUserType = forceUserType || userType;
    const isB2B = product.isB2B !== undefined ? product.isB2B : currentUserType === 'b2b';

    const cartProduct = {
      id:       product.id,
      name:     product.name     || 'Product',
      price:    product.price    || 0,
      qty:      quantity         || 1,
      image:    product.image    || product.imageUrl || '🛒',
      category: product.category || 'General',
      unit:     product.unit     || '',
      minOrder: product.min_order || (isB2B ? 1 : 1), // Store minimum order
      isB2B,
    };

    if (isB2B) {
      setB2bCartItems(prev => {
        const existing = prev.find(i => i.id === cartProduct.id);
        if (existing) {
          // For existing B2B items, add only 1 unit
          showToast(`Added 1 × ${cartProduct.name} to B2B cart`);
          return prev.map(i => i.id === cartProduct.id ? { ...i, qty: i.qty + 1 } : i);
        } else {
          // For new B2B items, add the full minimum quantity
          showToast(`Added ${cartProduct.qty} × ${cartProduct.name} to B2B cart`);
          return [...prev, cartProduct];
        }
      });
    } else {
      setCartItems(prev => {
        const existing = prev.find(i => i.id === cartProduct.id);
        return existing
          ? prev.map(i => i.id === cartProduct.id ? { ...i, qty: i.qty + cartProduct.qty } : i)
          : [...prev, cartProduct];
      });
      showToast(`${cartProduct.name} added to cart`);
    }
  }, [userType, showToast]);

  const removeFromCart = useCallback((id, isB2B = null) => {
    const b2b = isB2B !== null ? isB2B : userType === 'b2b';
    if (b2b) {
      setB2bCartItems(prev => prev.filter(i => i.id !== id));
      showToast('Item removed from B2B cart');
    } else {
      setCartItems(prev => prev.filter(i => i.id !== id));
      showToast('Item removed from cart');
    }
  }, [userType, showToast]);

  const updateQty = useCallback((id, delta, isB2B = null, minOrder = 1) => {
    const b2b = isB2B !== null ? isB2B : userType === 'b2b';
    const updater = prev => {
      return prev.map(i => {
        if (i.id === id) {
          const newQty = i.qty + delta;
          // For B2B items, clamp at minimum order — never go below
          if (b2b) {
            if (newQty < minOrder) {
              return { ...i, qty: minOrder }; // Stay at minimum, don't remove
            }
            return { ...i, qty: newQty };
          }
          // For regular items, if quantity would become 0 or less, remove the item
          if (newQty <= 0) {
            return null; // Mark for removal
          }
          return { ...i, qty: newQty };
        }
        return i;
      }).filter(Boolean); // Remove null items (B2C only)
    };
    
    if (b2b) {
      setB2bCartItems(updater);
    } else {
      setCartItems(updater);
      // Show toast for item removal
      const currentItems = cartItems;
      const item = currentItems.find(i => i.id === id);
      if (item && item.qty === 1 && delta === -1) {
        showToast(`${item.name} removed from cart`);
      }
    }
  }, [userType, showToast, cartItems]);

  // clearCart: pass userId to also wipe persisted keys after logout
  const clearCart = useCallback((isB2B = null, userId = null) => {
    const b2b = isB2B !== null ? isB2B : userType === 'b2b';
    if (b2b) {
      setB2bCartItems([]);
      if (userId) clearPersistedCart('b2b', userId);
    } else {
      setCartItems([]);
      if (userId) clearPersistedCart('b2c', userId);
    }
  }, [userType]);

  // logoutCart: clears in-memory state only, preserves persisted cart for re-login
  const logoutCart = useCallback(() => {
    setCartItems([]);
    setB2bCartItems([]);
  }, []);

  const getCartTotal = useCallback(() => {
    // Calculate total from both B2C and B2B carts
    const b2cTotal = cartItems.reduce((sum, i) => sum + i.price * i.qty, 0);
    const b2bTotal = b2bCartItems.reduce((sum, i) => sum + i.price * i.qty, 0);
    return b2cTotal + b2bTotal;
  }, [cartItems, b2bCartItems]);

  const getCartCount = useCallback(() => {
    // Count items from both B2C and B2B carts
    const b2cCount = cartItems.reduce((sum, i) => sum + i.qty, 0);
    const b2bCount = b2bCartItems.reduce((sum, i) => sum + i.qty, 0);
    return b2cCount + b2bCount;
  }, [cartItems, b2bCartItems]);

  const switchUserType = useCallback((type, options = {}) => {
    const next = type === 'b2b' ? 'b2b' : 'b2c';
    setUserType(prev => {
      if (prev === next) return prev;
      return next;
    });
    setIsCartOpen(false);
    if (!options.silent) {
      showToast(`Switched to ${next === 'b2b' ? 'B2B Wholesale' : 'B2C Retail'} mode`);
    }
  }, [showToast]);

  const getDeliveryCharge = useCallback(() => {
    const total = getCartTotal();
    const hasB2BItems = b2bCartItems.length > 0;
    const hasB2CItems = cartItems.length > 0;
    
    // If only B2C items, delivery is free
    if (hasB2CItems && !hasB2BItems) {
      return 0;
    }
    
    // If only B2B items or mixed cart, use B2B delivery logic
    if (hasB2BItems) {
      if (total >= 50000) return 0;
      if (total >= 20000) return 100;
      if (total >= 5000)  return 200;
      return 300;
    }
    
    return 0; // Default free delivery
  }, [getCartTotal, b2bCartItems.length, cartItems.length]);

  const getFinalTotal = useCallback(() => {
    return getCartTotal() + getDeliveryCharge();
  }, [getCartTotal, getDeliveryCharge]);

  const getItemQuantity = useCallback((id) => {
    // Check both B2C and B2B carts for the item
    const b2cItem = cartItems.find(i => i.id === id);
    const b2bItem = b2bCartItems.find(i => i.id === id);
    
    if (b2cItem && b2bItem) {
      // If item exists in both carts, return combined quantity
      return b2cItem.qty + b2bItem.qty;
    }
    
    return b2cItem ? b2cItem.qty : (b2bItem ? b2bItem.qty : 0);
  }, [cartItems, b2bCartItems]);

  const isCartEmpty = useCallback(() => {
    return cartItems.length === 0 && b2bCartItems.length === 0;
  }, [cartItems.length, b2bCartItems.length]);

  const getCartSummary = useCallback(() => {
    const combinedCart = getCurrentCart();
    return {
      itemCount: combinedCart.reduce((s, i) => s + i.qty, 0),
      subtotal:  getCartTotal(),
      delivery:  getDeliveryCharge(),
      total:     getFinalTotal(),
      hasB2B:    b2bCartItems.length > 0,
      hasB2C:    cartItems.length > 0,
      isMixed:   b2bCartItems.length > 0 && cartItems.length > 0,
    };
  }, [getCartTotal, getDeliveryCharge, getFinalTotal, getCurrentCart, b2bCartItems.length, cartItems.length]);

  const value = {
    cartItems: getCurrentCart(),
    b2cCartItems: cartItems,
    b2bCartItems,
    cartCount: getCartCount(),
    cartTotal: getCartTotal(),
    isCartOpen, setIsCartOpen,
    toast, userType,
    addToCart, removeFromCart, updateQty, clearCart, logoutCart,
    getItemQuantity, isCartEmpty, getCartSummary,
    getDeliveryCharge, getFinalTotal, switchUserType,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
}
